import { EnumValue, isArray, RecordValue, StringValue } from '@coveo/bueno';
/**
 * The available sort orders.
 */
export var SortOrder;
(function (SortOrder) {
    SortOrder["Ascending"] = "ascending";
    SortOrder["Descending"] = "descending";
})(SortOrder || (SortOrder = {}));
/**
 * The available criteria that can be used to sort query results.
 */
export var SortBy;
(function (SortBy) {
    /**
     * Uses standard index ranking factors (adjacency, TDIDF) and custom ranking expressions (QREs and QRFs) to compute a ranking score for each query result item, and sorts the query results by descending score value.
     */
    SortBy["Relevancy"] = "relevancy";
    /**
     * Uses only custom ranking expressions (QREs and QRFs) to compute a ranking score for each query result item, and sorts the query results by descending score value.
     */
    SortBy["QRE"] = "qre";
    /**
     * Uses the date field to sort the query results. This field typically contains the last modification date of each item. May be in ascending or descending order.
     */
    SortBy["Date"] = "date";
    /**
     * Uses the value of a specific sortable field to sort the query results. May be in ascending or descending order.
     */
    SortBy["Field"] = "field";
    /**
     * Does not sort the query results; the index will return result items in an essentially random order.
     */
    SortBy["NoSort"] = "nosort";
})(SortBy || (SortBy = {}));
/**
 * Builds a sort expression that can be understood and executed by the Coveo Platform.
 * @param criterion The criterion to translate to a valid sort query expression.
 */
export const buildCriterionExpression = (criterion) => {
    if (isArray(criterion)) {
        return criterion.map((c) => buildCriterionExpression(c)).join(',');
    }
    switch (criterion.by) {
        case SortBy.Relevancy:
        case SortBy.QRE:
        case SortBy.NoSort:
            return criterion.by;
        case SortBy.Date:
            return `date ${criterion.order}`;
        case SortBy.Field:
            return `@${criterion.field} ${criterion.order}`;
        default:
            return '';
    }
};
/**
 * Utility function that builds a valid `SortByRelevancy` criterion.
 * @returns A `SortByRelevancy` criterion.
 */
export const buildRelevanceSortCriterion = () => ({
    by: SortBy.Relevancy,
});
/**
 * Utility function that builds a valid `SortByDate` criterion.
 * @param order The order (ascending/descending) on which to sort.
 * @returns A `SortByDate` criterion.
 */
export const buildDateSortCriterion = (order) => ({
    by: SortBy.Date,
    order,
});
/**
 * Utility function that builds a valid `SortByField` criterion.
 * @param field The sortable field on which to sort.
 * @param order The order (ascending/descending) on which to sort.
 * @returns A `SortByField` criterion.
 */
export const buildFieldSortCriterion = (field, order) => ({
    by: SortBy.Field,
    order,
    field,
});
/**
 * Utility function that builds a valid `SortByQRE` criterion.
 * @returns A `SortByQRE` criterion.
 */
export const buildQueryRankingExpressionSortCriterion = () => ({
    by: SortBy.QRE,
});
/**
 * Utility function that builds a valid `SortByNoSort` criterion.
 * @returns A `SortByNoSort` criterion.
 */
export const buildNoSortCriterion = () => ({ by: SortBy.NoSort });
export const criterionDefinition = new RecordValue({
    values: {
        by: new EnumValue({ enum: SortBy, required: true }),
        order: new EnumValue({ enum: SortOrder }),
        field: new StringValue(),
    },
});
